import {
  AuthDesktopCard,
  AuthDesktopLayout,
  AuthScreenContent,
  AuthScreenFooter,
  AuthScreenHeader,
  AuthScreenLayout,
  AuthScreenSpacer,
  authScreenStyles,
} from "@/components/auth/auth-screen-layout";
import { ThemedText } from "@/components/themed-text";
import { BrandButton } from "@/components/ui/brand-button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { OAuth } from "@/components/ui/oauth";
import { TermsOfUseLink } from "@/components/ui/terms-of-use-link";
import { UserFormFields } from "@/components/user-form";
import { BrandColors, Colors } from "@/constants/theme";
import { useDesktopLayout } from "@/hooks/use-desktop-layout";
import {
  REGISTRATION_LABELS,
  isAppRole,
  isRegistrationType,
} from "@/lib/roles";
import { RegisterFormData, registerSchema } from "@/lib/validation/auth";
import { zodResolver } from "@hookform/resolvers/zod";
import Ionicons from "@react-native-vector-icons/ionicons";
import { Link, router, useLocalSearchParams } from "expo-router";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  StyleSheet,
  TouchableOpacity,
  View,
  useColorScheme,
} from "react-native";
import {
  Checkbox,
  HelperText,
  TextInput as PaperTextInput,
} from "react-native-paper";
import { useAuth } from "../../contexts/auth-context";
import { showToast } from "../../lib/toast";

const desktopSteps: {
  label: string;
  fields: (keyof RegisterFormData)[];
}[] = [
  {
    label: "Personal details",
    fields: ["first_name", "middle_name", "last_name"],
  },
  {
    label: "Contact details",
    fields: ["email", "phone", "address"],
  },
  {
    label: "Security",
    fields: ["referral_code", "password", "password_confirmation", "terms"],
  },
];

export default function RegisterDetailsScreen() {
  const { signUp } = useAuth();
  const {
    ref,
    role: roleParam,
    registration_type: registrationTypeParam,
  } = useLocalSearchParams<{
    ref?: string;
    role?: string;
    registration_type?: string;
  }>();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [desktopStep, setDesktopStep] = useState(0);
  const desktop = useDesktopLayout();
  const colorScheme = useColorScheme();
  const theme = Colors[colorScheme ?? "light"];

  const registrationType = isRegistrationType(registrationTypeParam)
    ? registrationTypeParam
    : undefined;
  const role = isAppRole(roleParam) ? roleParam : undefined;

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    setError,
    trigger,
    formState: { errors, isSubmitting },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      first_name: "",
      middle_name: "",
      last_name: "",
      email: "",
      phone: "",
      password: "",
      password_confirmation: "",
      role: role ?? "customer",
      address: "",
      referral_code: ref || "",
      origin_city: "",
      origin_latitude: undefined,
      origin_longitude: undefined,
      terms: false,
    },
  });

  useEffect(() => {
    if (!registrationType || !role) {
      router.replace({
        pathname: "/(auth)/register",
        ...(ref ? { params: { ref } } : {}),
      });
    }
  }, [registrationType, role, ref]);

  useEffect(() => {
    if (role) {
      setValue("role", role);
    }
  }, [role, setValue]);

  useEffect(() => {
    if (ref) {
      setValue("referral_code", ref);
    }
  }, [ref, setValue]);

  const formRole = watch("role");
  const registrationLabel = registrationType
    ? REGISTRATION_LABELS[registrationType]
    : "account";

  const goToFirstDesktopStepWithError = (
    hasError: (field: keyof RegisterFormData) => boolean,
  ) => {
    const firstInvalidStep = desktopSteps.findIndex((step) =>
      step.fields.some(hasError),
    );
    if (firstInvalidStep >= 0) {
      setDesktopStep(firstInvalidStep);
    }
  };

  const onSubmit = async (data: RegisterFormData) => {
    try {
      await signUp({
        first_name: data.first_name,
        middle_name: data.middle_name || undefined,
        last_name: data.last_name,
        email: data.email,
        phone: data.phone,
        password: data.password,
        password_confirmation: data.password_confirmation,
        role: data.role,
        address: data.address,
        referral_code: data.referral_code || undefined,
        origin_city: data.origin_city,
        origin_latitude: data.origin_latitude,
        origin_longitude: data.origin_longitude,
      });
      showToast.success(
        "Registration successful. Check your email for a verification code",
      );
      return true;
    } catch (error: any) {
      const serverErrors: Record<string, string[]> | undefined =
        error.response?.data?.errors;
      if (serverErrors) {
        desktopSteps.forEach((step) =>
          step.fields.forEach((field) => {
            const message = serverErrors[field]?.[0];
            if (message) {
              setError(field, { type: "server", message });
            }
          }),
        );
        goToFirstDesktopStepWithError((field) => !!serverErrors[field]);
      }

      const errorMessage =
        error.response?.data?.message || "Registration failed";
      showToast.error(errorMessage);
    }
  };

  const handleBack = () => {
    router.back();
  };

  const isLastDesktopStep = desktopStep === desktopSteps.length - 1;

  const handleDesktopNext = async () => {
    const isStepValid = await trigger(desktopSteps[desktopStep].fields);
    if (isStepValid) {
      setDesktopStep(desktopStep + 1);
    }
  };

  const handleDesktopPrevious = () => {
    if (desktopStep === 0) {
      handleBack();
      return;
    }
    setDesktopStep(desktopStep - 1);
  };

  const handleDesktopSubmit = handleSubmit(onSubmit, (formErrors) =>
    goToFirstDesktopStepWithError((field) => !!formErrors[field]),
  );

  const inputStyle = [
    authScreenStyles.input,
    desktop && authScreenStyles.desktopInput,
  ];
  const inputOutlineStyle = desktop
    ? authScreenStyles.desktopInputOutline
    : authScreenStyles.inputOutline;

  const securityFields = (
    <>
      <View style={authScreenStyles.field}>
        <Label label="Referral Code" required={false} />
        <Controller
          control={control}
          name="referral_code"
          render={({ field: { onChange, onBlur, value } }) => (
            <Input
              placeholder="Enter referral code"
              value={value}
              onChangeText={onChange}
              onBlur={onBlur}
              autoCapitalize="characters"
              autoComplete="off"
              textContentType="none"
              error={!!errors.referral_code}
              style={inputStyle}
              outlineStyle={inputOutlineStyle}
              testID="referral-code"
            />
          )}
        />
        {errors.referral_code && (
          <HelperText type="error" visible={!!errors.referral_code}>
            {errors.referral_code?.message}
          </HelperText>
        )}
      </View>

      <View style={authScreenStyles.field}>
        <Label label="Password" />
        <Controller
          control={control}
          name="password"
          render={({ field: { onChange, onBlur, value } }) => (
            <Input
              placeholder="Enter your password"
              value={value}
              onChangeText={onChange}
              onBlur={onBlur}
              secureTextEntry={!showPassword}
              autoComplete="off"
              textContentType="newPassword"
              error={!!errors.password}
              style={inputStyle}
              outlineStyle={inputOutlineStyle}
              right={
                <PaperTextInput.Icon
                  icon={showPassword ? "eye-off" : "eye"}
                  onPress={() => setShowPassword(!showPassword)}
                />
              }
              testID="password"
            />
          )}
        />
        {errors.password && (
          <HelperText type="error" visible={!!errors.password}>
            {errors.password?.message}
          </HelperText>
        )}
      </View>

      <View style={authScreenStyles.field}>
        <Label label="Confirm Password" />
        <Controller
          control={control}
          name="password_confirmation"
          render={({ field: { onChange, onBlur, value } }) => (
            <Input
              placeholder="Confirm your password"
              value={value}
              onChangeText={onChange}
              onBlur={onBlur}
              secureTextEntry={!showConfirmPassword}
              autoComplete="off"
              textContentType="newPassword"
              error={!!errors.password_confirmation}
              style={inputStyle}
              outlineStyle={inputOutlineStyle}
              right={
                <PaperTextInput.Icon
                  icon={showConfirmPassword ? "eye-off" : "eye"}
                  onPress={() => setShowConfirmPassword(!showConfirmPassword)}
                />
              }
              testID="confirm-password"
            />
          )}
        />
        {errors.password_confirmation && (
          <HelperText type="error" visible={!!errors.password_confirmation}>
            {errors.password_confirmation?.message}
          </HelperText>
        )}
      </View>

      <View>
        <Controller
          control={control}
          name="terms"
          render={({ field: { value } }) => (
            <View style={styles.termsRow}>
              <Checkbox.Android
                status={value ? "checked" : "unchecked"}
                onPress={() => setValue("terms", !value)}
                color={BrandColors.primary}
                uncheckedColor={BrandColors.primary}
                testID="terms-of-service"
              />
              <TermsOfUseLink label="I agree to the" />
            </View>
          )}
        />
        {errors.terms && (
          <HelperText type="error" visible={!!errors.terms}>
            {errors.terms?.message}
          </HelperText>
        )}
      </View>
    </>
  );

  const loginFooter = (
    <AuthScreenFooter>
      <ThemedText style={authScreenStyles.footerText} type="small">
        Already have an account?{" "}
      </ThemedText>
      <Link href="/(auth)" asChild>
        <TouchableOpacity accessibilityLabel="goto-login">
          <ThemedText style={authScreenStyles.link} type="smallSemiBold">
            Sign In
          </ThemedText>
        </TouchableOpacity>
      </Link>
    </AuthScreenFooter>
  );

  if (desktop) {
    const currentStep = desktopSteps[desktopStep];

    return (
      <AuthDesktopLayout onBack={handleBack}>
        <View style={styles.desktopHeaderRow}>
          <ThemedText style={styles.desktopTitle}>Create Account</ThemedText>
          {registrationType ? (
            <View style={styles.desktopBadge}>
              <ThemedText style={styles.desktopBadgeText}>
                {registrationLabel}
              </ThemedText>
            </View>
          ) : null}
        </View>

        <View style={[styles.stepper, { borderColor: theme.border }]}>
          {desktopSteps.map((step, index) => {
            const isReached = index <= desktopStep;

            return (
              <React.Fragment key={step.label}>
                {index > 0 && (
                  <ThemedText style={styles.stepSeparator}>·</ThemedText>
                )}
                <View
                  style={styles.stepItem}
                  accessibilityState={{ selected: index === desktopStep }}
                >
                  <View
                    style={[
                      styles.stepNumber,
                      isReached && styles.stepNumberActive,
                    ]}
                  >
                    <ThemedText
                      style={[
                        styles.stepNumberText,
                        isReached && styles.stepNumberTextActive,
                      ]}
                    >
                      {index + 1}
                    </ThemedText>
                  </View>
                  <ThemedText
                    style={[
                      styles.stepLabel,
                      isReached && styles.stepLabelActive,
                    ]}
                  >
                    {step.label}
                  </ThemedText>
                </View>
              </React.Fragment>
            );
          })}
        </View>

        <AuthDesktopCard>
          {currentStep.label === "Security" ? (
            <View style={authScreenStyles.desktopForm}>{securityFields}</View>
          ) : (
            <UserFormFields
              control={control}
              errors={errors}
              setValue={setValue}
              role={formRole}
              section={desktopStep === 0 ? "name" : "contact"}
              style={authScreenStyles.desktopForm}
              inputStyle={authScreenStyles.desktopInput}
              inputOutlineStyle={authScreenStyles.desktopInputOutline}
            />
          )}

          <View style={styles.desktopActions}>
            <TouchableOpacity
              onPress={handleDesktopPrevious}
              style={[styles.desktopBackButton, { borderColor: theme.border }]}
              accessibilityRole="button"
              accessibilityLabel="Previous step"
            >
              <Ionicons
                name="chevron-back"
                size={14}
                color={BrandColors.mediumGray}
              />
              <ThemedText style={styles.desktopBackText}>Back</ThemedText>
            </TouchableOpacity>

            <BrandButton
              variant="primary"
              loading={isSubmitting}
              onPress={
                isLastDesktopStep ? handleDesktopSubmit : handleDesktopNext
              }
              style={[
                authScreenStyles.desktopContinueButton,
                styles.desktopContinueButton,
              ]}
              labelStyle={authScreenStyles.desktopContinueButtonLabel}
              contentStyle={authScreenStyles.continueButtonContent}
              icon={() => (
                <Ionicons
                  name="chevron-forward"
                  size={14}
                  color={BrandColors.white}
                />
              )}
            >
              {!isLastDesktopStep
                ? "Continue"
                : isSubmitting
                  ? "Creating Account..."
                  : "Sign Up"}
            </BrandButton>
          </View>
        </AuthDesktopCard>

        <View style={styles.desktopFooter}>{loginFooter}</View>
      </AuthDesktopLayout>
    );
  }

  return (
    <AuthScreenLayout onBack={handleBack} keyboardInset>
      <AuthScreenContent>
        <AuthScreenHeader
          title="Create Account"
          subtitle={`Complete your ${registrationLabel.toLowerCase()} registration`}
        />

        {registrationType ? (
          <View style={styles.selectionBadge}>
            <ThemedText style={styles.selectionBadgeText}>
              {registrationLabel}
            </ThemedText>
          </View>
        ) : null}

        <OAuth role={role} />

        <View style={authScreenStyles.form}>
          <UserFormFields
            control={control}
            errors={errors}
            setValue={setValue}
            role={formRole}
          />

          {securityFields}
        </View>
      </AuthScreenContent>

      <AuthScreenSpacer />

      <AuthScreenContent>
        <BrandButton
          variant="primary"
          loading={isSubmitting}
          onPress={handleSubmit(onSubmit)}
          style={authScreenStyles.continueButton}
          labelStyle={authScreenStyles.continueButtonLabel}
          contentStyle={authScreenStyles.continueButtonContent}
          icon={() => (
            <Ionicons
              name="chevron-forward"
              size={18}
              color={BrandColors.white}
            />
          )}
        >
          {isSubmitting ? "Creating Account..." : "Sign Up"}
        </BrandButton>
      </AuthScreenContent>

      {loginFooter}
    </AuthScreenLayout>
  );
}

const styles = StyleSheet.create({
  selectionBadge: {
    alignSelf: "flex-start",
    backgroundColor: BrandColors.primaryLight,
    borderRadius: 999,
    paddingHorizontal: 12,
    paddingVertical: 6,
    marginBottom: 8,
  },
  selectionBadgeText: {
    color: BrandColors.primary,
    fontSize: 14,
    fontWeight: "600",
  },
  termsRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  desktopHeaderRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 12,
    marginBottom: 12,
  },
  desktopTitle: {
    fontSize: 22,
    lineHeight: 30,
    fontWeight: "500",
  },
  desktopBadge: {
    backgroundColor: BrandColors.primaryLight,
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 3,
  },
  desktopBadgeText: {
    color: BrandColors.primary,
    fontSize: 12,
    lineHeight: 16,
    fontWeight: "500",
  },
  stepper: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
    gap: 6,
    borderWidth: 1,
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 4,
    marginBottom: 20,
  },
  stepItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  stepNumber: {
    width: 14,
    height: 14,
    borderRadius: 7,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: BrandColors.lightGray,
  },
  stepNumberActive: {
    backgroundColor: BrandColors.primary,
  },
  stepNumberText: {
    fontSize: 9,
    lineHeight: 12,
    color: BrandColors.mediumGray,
  },
  stepNumberTextActive: {
    color: BrandColors.white,
  },
  stepLabel: {
    fontSize: 11,
    lineHeight: 14,
    color: BrandColors.mediumGray,
  },
  stepLabelActive: {
    color: BrandColors.primary,
  },
  stepSeparator: {
    fontSize: 11,
    lineHeight: 14,
    color: BrandColors.mediumGray,
  },
  desktopActions: {
    flexDirection: "row",
    gap: 8,
    marginTop: 20,
    marginBottom: 4,
  },
  desktopBackButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    paddingHorizontal: 16,
    borderWidth: 1,
    borderRadius: 6,
  },
  desktopBackText: {
    fontSize: 14,
    lineHeight: 20,
    color: BrandColors.mediumGray,
  },
  desktopContinueButton: {
    flex: 1,
    marginBottom: 0,
  },
  desktopFooter: {
    marginTop: 16,
  },
});
